import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { prefs as prefsApi, type UserPrefs } from '../lib/api.js';
import { useAuth } from './AuthContext.js';

interface PrefsContextValue {
  prefs: UserPrefs;
  setPrefs: (updates: Partial<UserPrefs>) => Promise<void>;
}

export const DEFAULT_PREFS: UserPrefs = {
  theme: 'ambient-calm',
  themeMode: 'system',
  density: 'comfortable',
  locale: 'en-US',
  timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
  reducedMotion: false,
  highContrast: false,
  largeText: false,
  colorBlindMode: 'none',
};

const PrefsContext = createContext<PrefsContextValue | null>(null);

export function applyPrefsToDOM(p: UserPrefs): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const mode =
    p.themeMode === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : p.themeMode;
  root.setAttribute('data-theme', p.theme);
  root.setAttribute('data-mode', mode);
  if (p.reducedMotion) root.setAttribute('data-reduced-motion', '');
  else root.removeAttribute('data-reduced-motion');
  if (p.largeText) root.setAttribute('data-large-text', '');
  else root.removeAttribute('data-large-text');
  if (p.highContrast) root.setAttribute('data-high-contrast', '');
  else root.removeAttribute('data-high-contrast');
  if (p.colorBlindMode && p.colorBlindMode !== 'none')
    root.setAttribute('data-color-blind', p.colorBlindMode);
  else root.removeAttribute('data-color-blind');

  try {
    localStorage.setItem('aethercasa_theme', p.theme);
    localStorage.setItem('aethercasa_mode', p.themeMode);
    localStorage.setItem(
      'aethercasa_a11y',
      JSON.stringify({
        reducedMotion: p.reducedMotion,
        largeText: p.largeText,
        highContrast: p.highContrast,
        colorBlindMode: p.colorBlindMode,
      }),
    );
  } catch {
    /* ignore */
  }
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const { accessToken } = useAuth();
  const [prefsState, setPrefsState] = useState<UserPrefs>(DEFAULT_PREFS);

  useEffect(() => {
    if (!accessToken) return;
    prefsApi
      .get()
      .then((p) => {
        setPrefsState(p);
        applyPrefsToDOM(p);
      })
      .catch(() => {
        /* use defaults */
      });
  }, [accessToken]);

  const setPrefs = useCallback(
    async (updates: Partial<UserPrefs>) => {
      const newPrefs = { ...prefsState, ...updates };
      setPrefsState(newPrefs);
      applyPrefsToDOM(newPrefs);
      try {
        await prefsApi.put(updates);
      } catch {
        // non-fatal
      }
    },
    [prefsState],
  );

  return (
    <PrefsContext.Provider value={{ prefs: prefsState, setPrefs }}>
      {children}
    </PrefsContext.Provider>
  );
}

export function usePrefs(): PrefsContextValue {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error('usePrefs must be used within PrefsProvider');
  return ctx;
}
